/**
 * Stable Clerk auth subscription.
 *
 * Clerk's own useAuth() re-renders every consumer on EVERY session token
 * refresh (~50s): its context value is memoized on `sessionClaims.__raw`,
 * the raw JWT string, which changes on each refresh. With Clerk useAuth()
 * called from the root layout, all four root context providers and every
 * useApiClient consumer, each refresh re-rendered the entire app tree —
 * the source of the recurring "idle tab burns CPU / UI freeze" class
 * (Sentry: "UI freeze NNNNms on /dashboard|/records|/prompt-builder").
 *
 * useClerkGate() subscribes to the clerk instance directly and snapshots
 * only the primitives the app actually gates on. Consumers re-render when
 * load status or the signed-in user changes — NOT on token refresh.
 *
 * useStableGetToken() returns a referentially stable async getter with the
 * same semantics as Clerk's getToken (waits for load, null when signed out).
 */
import { useCallback, useRef, useSyncExternalStore } from 'react';
import { useClerk } from '@clerk/clerk-react';

export interface ClerkGateState {
  isLoaded: boolean;
  isSignedIn: boolean;
  userId: string | null;
  email: string | null;
  name: string | null;
}

// Minimal structural view of the clerk instance — keeps us off LoadedClerk's
// `loaded: true` literal type and decoupled from SDK type churn.
//
// NOTE: this hook depends on two clerk-js/clerk-react behaviors that are
// stable but not part of the typed public contract (verified against the
// pinned @clerk/clerk-react version — re-verify on upgrade):
// 1. useClerk()'s context value identity does NOT change on token refresh
//    (only on load-status transitions) — otherwise subscribe/getSnapshot
//    would be recreated per refresh and the render storm would return.
// 2. clerk.addListener() invokes the new listener immediately with the
//    current state, and clerk-react queues + replays pre-load listeners at
//    hydration — useStableGetToken's wait-for-load relies on this.
interface ClerkInstanceLike {
  loaded?: boolean;
  user?: {
    id: string;
    fullName: string | null;
    firstName: string | null;
    primaryEmailAddress?: { emailAddress: string } | null;
  } | null;
  session?: {
    // 'active' | 'pending' | 'expired' | ... — see Clerk SessionStatus.
    status?: string;
    getToken: () => Promise<string | null>;
  } | null;
  addListener: (listener: (...args: unknown[]) => void) => () => void;
}

/**
 * Cast the (stable) clerk instance to a caller-supplied structural view.
 *
 * useClerk() subscribes to IsomorphicClerkContext, which only changes on clerk
 * status transitions (loading → ready) — never on token refresh. Consumers that
 * need a different slice of the un-typed instance (e.g. unsafeMetadata/update)
 * supply their own `T` rather than re-deriving the `useClerk() as unknown as …`
 * cast. See the NOTE above for the clerk-js behaviors this relies on.
 */
export function useClerkInstanceAs<T>(): T {
  return useClerk() as unknown as T;
}

function useClerkInstance(): ClerkInstanceLike {
  return useClerkInstanceAs<ClerkInstanceLike>();
}

export function useClerkGate(): ClerkGateState {
  const clerk = useClerkInstance();
  const cacheRef = useRef<{ key: string; state: ClerkGateState } | null>(null);

  const subscribe = useCallback(
    (onChange: () => void) => clerk.addListener(onChange),
    [clerk]
  );

  const getSnapshot = useCallback((): ClerkGateState => {
    const isLoaded = !!clerk.loaded;
    const user = clerk.user ?? null;
    const userId = user?.id ?? null;
    const email = user?.primaryEmailAddress?.emailAddress ?? null;
    const name = user?.fullName ?? user?.firstName ?? null;
    // Mirror Clerk useAuth() semantics (treatPendingAsSignedOut=true): a
    // session in 'pending' status (session tasks: forced MFA, org selection)
    // has a non-null clerk.user but is NOT signed in. status undefined means
    // an SDK/mock without the field — treat as active.
    const session = clerk.session ?? null;
    const sessionActive =
      !!session && (session.status === undefined || session.status === 'active');
    const isSignedIn = !!userId && sessionActive;
    const key = `${isLoaded}|${isSignedIn}|${userId ?? ''}|${email ?? ''}|${name ?? ''}`;
    // Cache the object so identical auth state yields an identical reference —
    // useSyncExternalStore then skips the re-render on token-refresh emits.
    if (cacheRef.current?.key !== key) {
      cacheRef.current = {
        key,
        state: { isLoaded, isSignedIn, userId, email, name },
      };
    }
    return cacheRef.current.state;
  }, [clerk]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useStableGetToken(): () => Promise<string | null> {
  const clerk = useClerkInstance();

  return useCallback(async () => {
    if (!clerk.loaded) {
      // Same contract as Clerk's getToken(): wait for clerk-js to load.
      await new Promise<void>((resolve) => {
        const unsubscribe = clerk.addListener(() => {
          if (clerk.loaded) {
            unsubscribe();
            resolve();
          }
        });
      });
    }
    return clerk.session ? await clerk.session.getToken() : null;
  }, [clerk]);
}
